import React from 'react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import type { DialogSize } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

// Map legacy maxWidth values to the new standardized DialogSize system
const legacySizeMap: Record<string, DialogSize> = {
	sm: 'sm',
	md: 'sm',
	lg: 'md',
	xl: 'md',
	'2xl': 'md',
	'3xl': 'lg',
};

interface EntityModalProps {
	isOpen: boolean;
	onClose: () => void;
	onBack?: () => void;
	entityType: string;
	title?: string;
	children: React.ReactNode;
	/** @deprecated Use `size` instead. Kept for backward compatibility. */
	maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
	size?: DialogSize;
	showBackButton?: boolean;
	noScrollArea?: boolean;
	hideTitle?: boolean;
	hideCloseButton?: boolean;
}

export function EntityModal({
	isOpen,
	onClose,
	onBack,
	entityType,
	title,
	children,
	maxWidth,
	size,
	showBackButton = false,
	noScrollArea = false,
	hideTitle = false,
	hideCloseButton = false
}: EntityModalProps) {
	const displayTitle = title || `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Details`;

	// Resolve size: prefer explicit `size`, fallback to mapped `maxWidth`, default to 'md'
	const resolvedSize: DialogSize = size ?? (maxWidth ? legacySizeMap[maxWidth] : 'md');

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				className={cn(
					"p-0 sm:p-0 overflow-hidden !flex flex-col",
					hideTitle && "h-[100dvh] sm:h-[85vh]"
				)}
				size={resolvedSize}
				enablePreRender={true}
				showCloseButton={!hideCloseButton}
			>
				{!hideTitle && (
					<DialogHeader className="px-5 sm:px-6 py-4 border-b flex-shrink-0 relative">
						{showBackButton && onBack && (
							<Button
								variant="ghost"
								size="sm"
								onClick={onBack}
								className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
							>
								<ArrowLeft className="h-4 w-4" />
								<span className="sr-only">Back</span>
							</Button>
						)}
						<DialogTitle className={cn(
							"text-lg font-semibold",
							showBackButton ? "text-center" : ""
						)}>
							{displayTitle}
						</DialogTitle>
					</DialogHeader>
				)}
				{noScrollArea ? (
					<div className="flex-1 overflow-hidden min-h-0 h-[calc(100dvh-64px)] sm:h-auto sm:max-h-[calc(85vh-64px)]">
						{children}
					</div>
				) : hideTitle ? (
					<div data-entity-modal className="flex-1 overflow-hidden min-h-0 p-5 sm:p-6">
						{children}
					</div>
				) : (
					<ScrollArea className="h-[calc(100dvh-64px)] sm:h-auto sm:max-h-[calc(85vh-64px)]">
						<div className="p-5 sm:p-6">
							{children}
						</div>
					</ScrollArea>
				)}
			</DialogContent>
		</Dialog>
	);
}
